import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { CSSTransitionGroup } from 'react-transition-group';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>
    <Col sm={3} smOffset={5} className="table-header">
      <Button
        bsStyle="primary"
        type="submit"
        className="relative-padding"
        onClick={props.addButtonClick}
      >
        Add Insight Set
      </Button>
    </Col>
  </Row>
);

const TableHeader = props => (
  <div>
    <Row>
      <Col sm={4} className="table-header">
        Insight Set name
      </Col>
      <Col sm={3} className="table-header">
        Date created
      </Col>
      <Col sm={3} className="table-header">
        Date updated
      </Col>
      <Col sm={2} className="table-header">
        Action
      </Col>
    </Row>
    <hr />
  </div>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Insight Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const { item, removeItem } = props;
  return (
    <Row key={item.id}>
      <Col sm={4} className="table-entry">
        {item.name}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(item.dateCreated, true)}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(item.dateUpdated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        {!item.shareWithAllIndustries && (
          <Button
            className="relative-padding"
            bsStyle="link"
            onClick={() => removeItem(item)}
            aria-label={`Remove ${item.name}`}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
};

class IndustryInsightSets extends React.Component {
  static displayName = 'IndustryInsightSets';

  constructor(props) {
    super(props);
    this.state = {
      newInsightSetId: undefined,
      showNewInsightId: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    this.loadIndustryInsights();

    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  loadIndustryInsights = () => {
    const industryId = this.props.params.industryId;
    this.props.getIndustryInsightSets(industryId, true);
    this.props.getIndustryInsightSets(industryId, false);
  };

  addInsightSet = () => {
    if (this.state.newInsightSetId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const industryId = parseInt(this.props.params.industryId, 10);
      const newInsightSetId = this.state.newInsightSetId;
      this.props
        .addInsightSetToIndustry(industryId, newInsightSetId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({
      showNewInsightId: false,
      newInsightSetId: undefined
    });
  };

  removeInsightSet = insightSet => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const industryId = parseInt(this.props.params.industryId, 10);
    this.props
      .removeInsightSetFromIndustry(insightSet, industryId)
      .then(response => this.success(response), error => this.failure(error));
  };

  success = response => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = error => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  updateSelectValue = newInsightSetId => {
    this.setState({ newInsightSetId });
  };

  cancelAddNew = () => {
    this.setState({ showNewInsightId: false, newInsightSetId: undefined });
  };

  addButtonClick = () => {
    this.setState({ showNewInsightId: !this.state.showNewInsightId });
  };

  render() {
    const { allocated, unallocated } = this.props.currentIndustry.insightSets;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeInsightSet} />
    );
    return (
      <div>
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing insight sets for industry {this.props.currentIndustry.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <TableHeader />

        {this.state.showNewInsightId && (
          <AddNewRow
            items={unallocated.filter(i => !i.isArchived)}
            selectValue={this.state.newInsightSetId}
            selectOnChange={this.updateSelectValue}
            addItem={this.addInsightSet}
            cancel={this.cancelAddNew}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="insight sets"
          tableHeader={TableHeader}
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default IndustryInsightSets;



// WEBPACK FOOTER //
// ./src/containers/industries/IndustryInsightSets.js